import { ReactNode, useEffect } from "react";
import { getData, useResource } from "../backend";
import { Template } from "../interfaces";
import { useTranslation } from "react-i18next";
import useSWRImmutable from 'swr/immutable';
import TemplateListCard from "./TemplateListCard";
import ScrollArrowButtons from "./ScrollArrowButtons";
import useHorizontalScroll from "../hooks/useHorizontalScroll";

function TemplateListRow(props: {
  header: ReactNode;
  templates: Template[];
  railWidth: string;
  headerClassName?: string;
}) {
  const { header, templates, railWidth, headerClassName } = props;
  const { ref, scroll, canScrollLeft, canScrollRight } = useHorizontalScroll();

  return (
    <>
      <div className={`flex items-center justify-between gap-4 flex-wrap ${headerClassName ?? ""}`}>
        {header}
        <ScrollArrowButtons
          size="small"
          canScrollLeft={canScrollLeft}
          canScrollRight={canScrollRight}
          onScrollLeft={() => scroll("left")}
          onScrollRight={() => scroll("right")}
        />
      </div>
      <div
        ref={ref}
        className="flex items-stretch overflow-x-scroll scrollbar-hidden gap-6 -mx-8 px-8 pr-28 [&>:last-child]:-mr-12"
        style={{ width: railWidth }}
      >
        {templates.map((template) =>
          <TemplateListCard key={template.id} template={template} />
        )}
      </div>
    </>
  );
}

function TemplateList() {
  const { data: templates } = useSWRImmutable<Array<Template>>("/templates/", getData);
  const { t } = useTranslation();

  const categories: Array<string> = useResource("/templates/categories/") ?? [];

  useEffect(() => {
    const pageTitle = t("Templates | Portant");
    if (document.title !== pageTitle)
      document.title = pageTitle;

    return (() => { document.title = "Portant" })
  }, [t])

  const categorySections = (!templates || categories.length === 0)
    ? []
    : categories
      .map((category) => ({
        category,
        templates: templates.filter((template) => (template?.categories ?? []).includes(category)),
      }))
      .filter((section) => section.templates.length > 0);

  const [featuredSection, ...standardSections] = categorySections;

  if (!templates || !categories)
    return <div className="m-auto"><img src="https://static.portant.co/portant-loading-gray.svg" className="w-32 h-32" alt="Loading Logo" /></div>;

  return (
    <div className="border border-gray-200 rounded-lg bg-white p-8 w-full flex flex-col gap-8">
      <p className="text-[16px] uppercase tracking-[0.1em] text-gray-500 font-semibold">
        {t("Template Library")}
      </p>

      {featuredSection &&
        <section className="relative rounded-lg border border-gray-200 bg-gray-50 p-8 flex flex-col gap-6">
          <span className="absolute -top-4 left-8 items-center px-2 py-1 rounded-md bg-lightblue text-blue text-[16px] font-semibold uppercase tracking-[0.05em]">
            {t("Featured")}
          </span>
          <TemplateListRow
            railWidth="calc(100vw - 336px)"
            headerClassName="pt-4"
            templates={featuredSection.templates}
            header={
              <div className="flex flex-col gap-2">
                <h2 className="text-[28px] leading-[32px] font-gilroy font-semibold text-gray-900">
                  {featuredSection.category}
                </h2>
                <p className="text-sm text-gray-500 max-w-2xl">
                  {t("Transform the documents required for your sales function into dynamic, automated workflows.")}
                </p>
              </div>
            }
          />
        </section>
      }
      <div className="flex flex-col">
        {standardSections.map((section, index) => (
          <section key={section.category} className={`flex flex-col gap-6 ${index === 0 ? "pt-0" : "pt-8"}`}>
            <TemplateListRow
              railWidth="calc(100vw - 270px)"
              templates={section.templates}
              header={
                <div>
                  <h3 className="text-[24px] leading-[28px] font-gilroy font-semibold text-gray-900">
                    {section.category}
                  </h3>
                  <p className="text-sm text-gray-500">{t("Automate Sales Contracts with HubSpot")}</p>
                </div>
              }
            />
          </section>
        ))}
      </div>
    </div>
  );
}

export default TemplateList;
